/**
 * Real-time microphone amplitude — the "ears amplitude" that drives the Jarvis
 * visualizer (ADR-0025). `SpeechRecognition` gives us *words*; this gives us the
 * *loudness* so the HUD can actually pulse with your voice while you speak.
 *
 * It taps the mic via Web Audio (`getUserMedia` → `AnalyserNode`) in PARALLEL to
 * the recognizer — two consumers of the same mic is fine — and exposes a cheap
 * `level()` returning a smoothed 0..1 RMS the render loop polls each frame.
 * Everything feature-detects + try/catches so a denied/absent mic degrades to a
 * synthetic animation rather than crashing the widget.
 */

export interface MicAnalyser {
  /** Smoothed instantaneous loudness, 0 (silence) … 1 (loud). Cheap to call per-frame. */
  level: () => number;
  /** Release the mic + close the audio graph. Idempotent-safe to call once. */
  stop: () => void;
}

/** Open a mic analyser, or resolve `null` if unavailable/denied (caller degrades). */
export async function startMicAnalyser(): Promise<MicAnalyser | null> {
  if (typeof window === "undefined" || typeof navigator === "undefined") return null;
  if (!navigator.mediaDevices?.getUserMedia) return null;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const Ctx =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) {
      stream.getTracks().forEach((tk) => tk.stop());
      return null;
    }
    const ctx = new Ctx();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.75;
    src.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    let smoothed = 0;
    return {
      level: () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128; // -1..1
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        // Attack fast, release slow — feels responsive without jitter.
        smoothed = rms > smoothed ? smoothed * 0.4 + rms * 0.6 : smoothed * 0.85 + rms * 0.15;
        return Math.min(1, smoothed * 3.2); // gain into a usable 0..1 range
      },
      stop: () => {
        stream.getTracks().forEach((tk) => tk.stop());
        ctx.close().catch(() => {
          /* already closed */
        });
      },
    };
  } catch {
    return null; // permission denied / no device → synthetic fallback
  }
}
